import { WHATSAPP_URL, depositSplit, upcomingTripDates } from "@majourneys/shared";

/**
 * Women-only group trips on the booking surfaces.
 *
 * An organiser runs the trip with female staff. The guest reserves a seat,
 * sends a deposit straight to the organiser (bank transfer or CashPlus, never
 * to us), and pays the rest on the day. We hold the seat for 48 hours while
 * the deposit arrives. This is the one activity surface that names a payment
 * before the day, by owner exception; nothing is ever charged online.
 */

/** The pink accent, kept for women-only pieces only. */
export const WOMEN_PINK = "#B8336A";
/** Ink for small text on the wash: about 8.9:1, AA at any size. */
export const WOMEN_INK = "#72243E";
export const WOMEN_WASH = "#FBEAF0";

/** How long a reserved seat is held while the deposit arrives. */
export const SEAT_HOLD_HOURS = 48;

export interface WomenTripOrganiser {
  slug: string;
  name: string;
  tagline: string | null;
  instagramHandle: string | null;
  avatarUrl: string | null;
}

/** `ActivityDetail.womenTrip`: null on every other activity. */
export interface WomenTripInfo {
  seatLimit: number | null;
  /** Per seat, MAD. */
  depositAmount: number | null;
  /** Upcoming dates; when non-empty, the only bookable ones. */
  tripDates: { date: string; seatsLeft: number | null }[];
  organiser: WomenTripOrganiser | null;
  /** Cities the trip leaves from. When non-empty the guest must pick one. */
  departures?: string[];
}

/** The departure cities a guest picks from, trimmed and without repeats. */
export function tripDepartureList(trip: Pick<WomenTripInfo, "departures"> | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of trip?.departures ?? []) {
    const city = typeof raw === "string" ? raw.trim() : "";
    if (city && !out.includes(city)) out.push(city);
  }
  return out;
}

/**
 * True when the departure step is answered: the trip lists none, or the guest
 * picked one of its cities. A city the trip no longer lists does not count,
 * since the server would refuse it (`trip_departure_required`).
 */
export function tripDepartureOk(
  trip: Pick<WomenTripInfo, "departures"> | null | undefined,
  picked: string | null | undefined,
): boolean {
  const list = tripDepartureList(trip);
  if (list.length === 0) return true;
  return Boolean(picked && list.includes(picked));
}

/** True when the trip runs on fixed dates (the calendar offers only those). */
export function hasFixedTripDates(trip: WomenTripInfo | null | undefined): trip is WomenTripInfo {
  return Boolean(trip && trip.tripDates.length > 0);
}

/** Every upcoming trip date, full ones included, soonest first. */
export function tripDateList(trip: WomenTripInfo | null | undefined, todayIso: string): string[] {
  if (!trip) return [];
  return upcomingTripDates(
    trip.tripDates.map((d) => d.date),
    todayIso,
  );
}

/** Upcoming trip dates that still have a seat. */
export function openTripDates(trip: WomenTripInfo | null | undefined, todayIso: string): string[] {
  if (!trip) return [];
  const full = new Set(trip.tripDates.filter((d) => d.seatsLeft === 0).map((d) => d.date));
  return tripDateList(trip, todayIso).filter((d) => !full.has(d));
}

/** A trip with dates, every one of them full: the page offers the waiting list. */
export function isTripFull(trip: WomenTripInfo | null | undefined, todayIso: string): boolean {
  return tripDateList(trip, todayIso).length > 0 && openTripDates(trip, todayIso).length === 0;
}

/**
 * Seats still free on a date. Null when the trip has no limit or the date is
 * not one of its dates (the server answers then, not the page).
 */
export function tripSeatsLeftOn(trip: WomenTripInfo | null | undefined, date: string | null | undefined): number | null {
  if (!trip || !date) return null;
  return trip.tripDates.find((d) => d.date === date)?.seatsLeft ?? null;
}

/** The fewest seats left on any upcoming date, for a card or a summary. */
export function fewestSeatsLeft(trip: WomenTripInfo | null | undefined, todayIso: string): number | null {
  if (!trip) return null;
  const upcoming = new Set(tripDateList(trip, todayIso));
  const counts = trip.tripDates
    .filter((d) => upcoming.has(d.date) && d.seatsLeft != null && d.seatsLeft > 0)
    .map((d) => d.seatsLeft as number);
  return counts.length > 0 ? Math.min(...counts) : null;
}

export interface TripMoneyRows {
  /** What the seats cost: the booking's total. */
  seat: number;
  /** Sent to the organiser to hold the seat. */
  deposit: number;
  /** Paid on the day. Always `seat - deposit`, so the rows add up. */
  rest: number;
}

/**
 * The three money rows of a trip: the seat, the deposit, the rest.
 *
 * `shownTotal` is the total as the page shows it (already converted and
 * rounded); `unit` converts and rounds one dirham price the same way. The
 * deposit is converted per seat, then multiplied, like every other line, and
 * the rest is the difference, so deposit + rest is the total on screen in
 * every currency, never one off from rounding. In dirhams this is exactly
 * `depositSplit` on the server. Null when the trip asks no deposit.
 */
export function tripMoneyRows(
  shownTotal: number,
  depositPerSeatMad: number | null | undefined,
  seats: number,
  unit: (mad: number) => number = (mad) => Math.round(mad),
): TripMoneyRows | null {
  if (depositPerSeatMad == null || depositPerSeatMad <= 0) return null;
  const split = depositSplit(shownTotal, unit(depositPerSeatMad), seats);
  if (!split) return null;
  return { seat: Math.max(0, Math.round(shownTotal)), deposit: split.deposit, rest: split.rest };
}

/** Our WhatsApp, with the waiting-list message already written. */
export function waitingListHref(message: string): string {
  return `${WHATSAPP_URL}?text=${encodeURIComponent(message)}`;
}

/** Where a guest is on the way to a confirmed seat: 0 Reserved, 1 Deposit received, 2 Confirmed. */
export function tripStep(input: { status: string; depositReceived: boolean }): 0 | 1 | 2 {
  const s = input.status.toLowerCase();
  if (s === "confirmed" || s === "completed" || s === "paid") return 2;
  return input.depositReceived ? 1 : 0;
}
